import React, { useState } from 'react';
import { Card, Table, Button, Input, Select, DatePicker, Space, Tag, Modal, Form, message } from 'antd';
import { PlusOutlined, SearchOutlined, EditOutlined, DeleteOutlined, PlayCircleOutlined, EyeOutlined } from '@ant-design/icons';
import moment from 'moment';

const { RangePicker } = DatePicker;
const { Option } = Select;

const VideoManagement = () => {
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState('add'); // 'add' or 'edit'
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [form] = Form.useForm();

  // 模拟数据
  const [dataSource, setDataSource] = useState([
    {
      id: 1,
      name: '前门监控',
      location: '大楼前门',
      type: 'IP摄像头',
      status: 'online',
      lastActive: '2025-03-18 15:10:23',
      ip: '192.168.1.101',
    },
    {
      id: 2,
      name: '后门监控',
      location: '大楼后门',
      type: 'IP摄像头',
      status: 'online',
      lastActive: '2025-03-18 15:12:45',
      ip: '192.168.1.102',
    },
    {
      id: 3,
      name: '停车场',
      location: '地下停车场',
      type: 'IP摄像头',
      status: 'offline',
      lastActive: '2025-03-18 10:30:15',
      ip: '192.168.1.103',
    },
    {
      id: 4,
      name: '电梯监控',
      location: '主楼电梯',
      type: 'IP摄像头',
      status: 'online',
      lastActive: '2025-03-18 15:15:33',
      ip: '192.168.1.104',
    },
    {
      id: 5,
      name: '仓库监控',
      location: '仓库区域',
      type: 'IP摄像头',
      status: 'maintenance',
      lastActive: '2025-03-17 18:45:20',
      ip: '192.168.1.105',
    },
  ]);

  // 表格列定义
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '摄像头名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '位置',
      dataIndex: 'location',
      key: 'location',
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        let color = 'green';
        let text = '在线';
        if (status === 'offline') {
          color = 'red';
          text = '离线';
        } else if (status === 'maintenance') {
          color = 'orange';
          text = '维护中';
        }
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: '最后活动时间',
      dataIndex: 'lastActive',
      key: 'lastActive',
      sorter: (a, b) => moment(a.lastActive).unix() - moment(b.lastActive).unix(),
    },
    {
      title: 'IP地址',
      dataIndex: 'ip',
      key: 'ip',
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      render: (_, record) => (
        <Space size="middle">
          <Button 
            type="primary" 
            icon={<PlayCircleOutlined />} 
            size="small"
            onClick={() => handleViewLive(record)}
          >
            实时查看
          </Button>
          <Button 
            icon={<EditOutlined />} 
            size="small"
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Button 
            danger 
            icon={<DeleteOutlined />} 
            size="small"
            onClick={() => handleDelete(record)}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  // 处理实时查看
  const handleViewLive = (record) => {
    message.info(`正在连接到摄像头：${record.name}`);
    // 实际项目中这里应该打开视频流查看界面
  };

  // 处理编辑
  const handleEdit = (record) => {
    setModalType('edit');
    setSelectedRecord(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  // 处理删除
  const handleDelete = (record) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除摄像头 "${record.name}" 吗？`,
      onOk() {
        setDataSource(dataSource.filter(item => item.id !== record.id));
        message.success('删除成功');
      },
    });
  };

  // 处理添加
  const handleAdd = () => {
    setModalType('add');
    form.resetFields();
    setModalVisible(true);
  };

  // 处理表单提交
  const handleFormSubmit = () => {
    form.validateFields().then(values => {
      if (modalType === 'add') {
        // 添加新摄像头
        const newCamera = {
          id: dataSource.length + 1,
          ...values,
          status: 'online',
          lastActive: moment().format('YYYY-MM-DD HH:mm:ss'),
        };
        setDataSource([...dataSource, newCamera]);
        message.success('添加成功');
      } else {
        // 更新现有摄像头
        const updatedDataSource = dataSource.map(item => {
          if (item.id === selectedRecord.id) {
            return { ...item, ...values };
          }
          return item;
        });
        setDataSource(updatedDataSource);
        message.success('更新成功');
      }
      setModalVisible(false);
    });
  };

  return (
    <div>
      <Card title="视频管理" extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          添加摄像头
        </Button>
      }>
        <div className="search-bar">
          <Space>
            <Input 
              placeholder="摄像头名称" 
              prefix={<SearchOutlined />} 
              style={{ width: 200 }}
            />
            <Select 
              placeholder="状态" 
              style={{ width: 120 }}
              allowClear
            >
              <Option value="online">在线</Option>
              <Option value="offline">离线</Option>
              <Option value="maintenance">维护中</Option>
            </Select>
            <RangePicker />
            <Button type="primary">搜索</Button>
            <Button>重置</Button>
          </Space>
        </div>
        
        <Table 
          columns={columns} 
          dataSource={dataSource} 
          rowKey="id" 
          loading={loading}
          pagination={{ pageSize: 10 }}
          className="custom-table"
        />
      </Card>

      {/* 添加/编辑摄像头模态框 */}
      <Modal
        title={modalType === 'add' ? '添加摄像头' : '编辑摄像头'}
        open={modalVisible}
        onOk={handleFormSubmit}
        onCancel={() => setModalVisible(false)}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="摄像头名称"
            rules={[{ required: true, message: '请输入摄像头名称' }]}
          >
            <Input placeholder="请输入摄像头名称" />
          </Form.Item>
          <Form.Item
            name="location"
            label="位置"
            rules={[{ required: true, message: '请输入位置' }]}
          >
            <Input placeholder="请输入位置" />
          </Form.Item>
          <Form.Item
            name="type"
            label="类型"
            rules={[{ required: true, message: '请选择类型' }]}
          >
            <Select placeholder="请选择类型">
              <Option value="IP摄像头">IP摄像头</Option>
              <Option value="网络摄像头">网络摄像头</Option>
              <Option value="USB摄像头">USB摄像头</Option>
              <Option value="高清摄像头">高清摄像头</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="ip"
            label="IP地址"
            rules={[{ required: true, message: '请输入IP地址' }]}
          >
            <Input placeholder="请输入IP地址" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default VideoManagement;
